import dayjs from 'dayjs';
import ShelfController from '../../../core/shelf.controller';
import { User } from '../../../data';
import { getTotalInvestment } from '../../../utils/tradeInvestment';
import { countStoresByChannel, filterChecksByStores, filterEntityByChannel } from '../../../utils/storeChannel';

class CampaignController extends ShelfController {
    async initialize() {
        let campaign = null;
        let contracts = null;

        if (this.isAuthenticated) {
            campaign = await this.data.fetchCampaignById(this.props.searchParams.get('id'));
            const storeChecks = await this.data.fetchStoreCheckByContractId(this.props.searchParams.get('id'));
            contracts = await this.filterContracts(storeChecks);
        } else this.navigateLogin();

        this.setState({ campaign, contracts, loading: false });
    }

    /** Guarda el precio de un piso puntual y refresca la campaña en pantalla. */
    async updateStorePrice(storeId, price) {
        try {
            const campaignId = this.props.searchParams.get('id');
            const campaign = await this.data.updateCampaignStorePrice(campaignId, storeId, price);
            if (campaign?._id) this.setState({ campaign });
        } catch (err) {
            console.log(err);
        }
    }

    async filterContracts(storeChecks) {
        let contracts = [];
        storeChecks?.forEach((storeCheck) => {
            const campaignData = storeCheck?.contracts?.find((contract) => contract?.tradeContractId === this.props.searchParams.get('id'));
            if (campaignData) {
                contracts.push({
                    ...campaignData,
                    storeId: storeCheck?.storeId,
                    createdAt: storeCheck?.createdAt, // Include timestamp from store check
                    updatedAt: storeCheck?.updatedAt
                });
            }
        });
        return contracts;
    }

    handleChannelChange(channel) {
        this.setState({ channel });
    }

    /** Campaña y visitas recortadas al canal seleccionado. */
    getFilteredData() {
        const { campaign, contracts, channel } = this.state;
        const filteredCampaign = filterEntityByChannel(campaign, channel);
        const filteredContracts = filterChecksByStores(contracts, filteredCampaign?.stores);
        return { filteredCampaign, filteredContracts };
    }

    handleEdit() {
        const campaignId = this.props.searchParams.get('id');
        this.props.navigate(`/dashboard/trade/campaigns?edit=${campaignId}`);
    }

    calculateAnalytics({ campaign, contracts }) {
        if (!campaign || !contracts) {
            return {
                totalStores: 0,
                uniqueStoresVisited: 0,
                totalVisits: 0,
                availableCount: 0,
                notAvailableCount: 0,
                complianceRate: 0,
                totalImages: 0,
                pendingImages: 0,
                materialsTracked: 0
            };
        }

        const totalStores = campaign.stores?.length || 0;

        const uniqueStoreIds = new Set();
        contracts?.forEach((contract) => {
            if (contract.storeId) {
                uniqueStoreIds.add(contract.storeId);
            }
        });
        const uniqueStoresVisited = uniqueStoreIds.size;

        const totalVisits = contracts?.length || 0;

        const availableVisitsCount = contracts?.filter((c) => c.available)?.length || 0;

        const storeLatestCheck = new Map();
        contracts?.forEach((contract) => {
            const existingCheck = storeLatestCheck.get(contract.storeId);
            if (!existingCheck || new Date(contract.createdAt) > new Date(existingCheck.createdAt)) {
                storeLatestCheck.set(contract.storeId, contract);
            }
        });

        const availableCount = Array.from(storeLatestCheck.values()).filter((c) => c.available).length;
        const notAvailableCount = uniqueStoresVisited - availableCount;
        const complianceRate = uniqueStoresVisited > 0 ? (availableCount / uniqueStoresVisited) * 100 : 0;

        let totalImages = 0;
        let pendingImages = 0;

        contracts?.forEach((contract) => {
            if (contract.images?.length) {
                contract.images.forEach((img) => {
                    if (img.url) {
                        totalImages++;
                    } else {
                        pendingImages++;
                    }
                });
            }
            if (contract.campaignContent?.length) {
                contract.campaignContent.forEach((content) => {
                    if (content.images?.length) {
                        content.images.forEach((img) => {
                            if (img.url) {
                                totalImages++;
                            } else {
                                pendingImages++;
                            }
                        });
                    }
                });
            }
        });

        const materialsTracked = campaign.content?.length || 0;

        return {
            totalStores,
            uniqueStoresVisited,
            totalVisits,
            availableCount,
            notAvailableCount,
            complianceRate,
            totalImages,
            pendingImages,
            materialsTracked,
            availableVisitsCount
        };
    }

    /** Everything the detail view renders, derived from state (channel-filtered). */
    getViewModel() {
        const { campaign } = this.state;
        const { filteredCampaign, filteredContracts } = this.getFilteredData();
        return {
            filteredCampaign,
            filteredContracts,
            analytics: this.calculateAnalytics({ campaign: filteredCampaign, contracts: filteredContracts }),
            isExpired: Boolean(campaign?.expirationDate && dayjs(campaign.expirationDate).isBefore(dayjs(), 'day')),
            isClientUser: User.getInstance()?.userType === 'client',
            totalInvestment: getTotalInvestment(filteredCampaign),
            channelCounts: countStoresByChannel(campaign?.stores),
            materials: (campaign?.content || []).map((c) => c.material).filter(Boolean)
        };
    }
}

export default CampaignController;
